import React from "react";
import Reveal from "./Reveal";
import TrainingRunAnimation from "./TrainingRunAnimation";

const trainingFacts = [
  {
    title: "Both sides count your data, and they have to agree.",
    text: "Your machine and the host count the dataset independently against the same frozen reference, so the price is arithmetic rather than trust. Neither side can quietly inflate the bill.",
  },
  {
    title: "Nothing is billed before the work is proven.",
    text: "A job turned away settles to zero and releases your deposit, whether the count did not reconcile, the dataset failed its scan, or the host was busy. A run that stops halfway bills for the slices that finished.",
  },
  {
    title: "The finished model stays private in use, too.",
    text: "Attach it to a chat and it is held for that session alone: invisible to anyone else on the same machine, and gone the moment the session ends.",
  },
  {
    title: "If your model is not being used, it says so.",
    text: "When an adapter cannot be loaded, the chat tells you it is answering from the base model instead, so you never quietly pay for a fine-tune you are not getting.",
  },
];

// Private fine-tuning (Training M0), presented as a shipping capability.
export default function TrainingSection() {
  return (
    <div
      id="training"
      className="mx-auto mt-24 max-w-7xl px-6 sm:mt-44 lg:px-8 scroll-mt-24"
    >
      <Reveal className="mx-auto max-w-2xl lg:text-center">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 lg:justify-center">
          <h2 className="text-base/7 font-semibold text-brand-lightpurple">
            Fabstir AI &middot; Private fine-tuning
          </h2>
          <span className="rounded-full bg-brand-pink/20 px-3 py-0.5 text-xs font-semibold text-brand-pink ring-1 ring-inset ring-brand-pink/30">
            New
          </span>
        </div>
        <p className="mt-2 text-pretty text-4xl font-semibold tracking-tight text-brand-pink sm:text-5xl lg:text-balance">
          Train an AI on your own work. Be the only one who can use it.
        </p>
        <p className="mt-6 text-lg/8 text-brand-gray">
          Your support history, your house style, your domain: the things that
          make a model worth having are exactly the things you cannot hand to an
          AI company. So don&rsquo;t. Your dataset is sealed before it leaves
          your machine, a GPU host is paid slice by slice for the work it
          actually does, and the finished model comes back encrypted under keys
          only you hold.
        </p>
      </Reveal>

      <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 items-center gap-10 sm:mt-20 lg:max-w-none lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <TrainingRunAnimation />
        </Reveal>
        <Reveal delay={150}>
          <h3 className="text-2xl font-semibold text-white sm:text-3xl">
            Nobody keeps a copy. Not of your data, not of what it learned
          </h3>
          <p className="mt-4 text-base/7 text-gray-300">
            An AI platform that fine-tunes for you ends up holding both halves:
            the corpus you trained on and the model it produced. On Fabstir
            there is no platform in the middle to hold either. The dataset goes
            out in encrypted shards, the model comes back the same way, and the
            only copy that survives the run is yours.
          </p>
          <p className="mt-4 text-base/7 text-gray-300">
            Training runs on the same rails as every other job here: the same
            escrow, the same per-slice receipts, the same refund of anything you
            did not spend.
          </p>
        </Reveal>
      </div>

      <div className="mx-auto mt-16 grid max-w-4xl grid-cols-1 gap-4 sm:mt-20 sm:grid-cols-2">
        {trainingFacts.map((fact, i) => (
          <Reveal key={fact.title} delay={(i % 2) * 120}>
            <div className="h-full rounded-xl bg-white/5 px-5 py-4 text-sm/6 text-gray-300 ring-1 ring-white/10">
              <span className="font-semibold text-white">{fact.title}</span>{" "}
              {fact.text}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}
